import type { TripSegment } from '@der-maschinfuehrer/contracts';

export function SavedSegmentCard({
  editingDisabled = false,
  onCopy,
  onEdit,
  onReverse,
  onSaveTemplate,
  segment,
}: {
  readonly editingDisabled?: boolean;
  readonly onCopy: () => void;
  readonly onEdit: () => void;
  readonly onReverse: () => void;
  readonly onSaveTemplate: () => void;
  readonly segment: TripSegment;
}) {
  const origin = segment.origin?.display_name ?? 'bez místa Odkud';
  const destination = segment.destination?.display_name ?? 'bez místa Kam';
  return (
    <article className="segment-card">
      <h4>
        {origin} → {destination}
      </h4>
      <dl className="segment-details">
        <Detail label="Stav" value={statusLabel(segment.status)} />
        <Detail label="Skutečný začátek" value={momentLabel(segment.actual_start)} />
        <Detail label="Skutečný konec" value={momentLabel(segment.actual_end)} />
        <Detail label="Vlak nebo výkon" value={segment.train_or_service_number ?? 'neuvedeno'} />
        <Detail label="Druh výkonu" value={workTypeLabel(segment.work_type)} />
        <Detail label="Role" value={roleLabel(segment.role)} />
        <Detail label="Dopravce" value={segment.operator ?? 'neuvedeno'} />
        <Detail
          label="Kilometráž"
          value={
            segment.distance_metres === null
              ? 'neuvedeno'
              : `${(segment.distance_metres / 1_000).toLocaleString('cs-CZ', { maximumFractionDigits: 3 })} km · ${distanceProvenanceLabel(segment)}`
          }
        />
        <Detail
          label="Trasa"
          value={
            segment.route_snapshot?.confirmation.kind === 'explicit_user_confirmation'
              ? 'výslovně potvrzená uživatelem'
              : 'nepotvrzená / neuvedená'
          }
        />
        <Detail label="Průběh trasy" value={routePathLabel(segment)} />
        <Detail label="Původ varianty" value={routeSourceLabel(segment)} />
        <Detail
          label="Verze katalogu"
          value={segment.data_versions.catalog?.version ?? 'bez katalogové provenance'}
        />
        {segment.status === 'partial' ? (
          <>
            <Detail
              label="Původně plánovaný cíl"
              value={segment.planned_destination?.display_name ?? 'neuvedeno'}
            />
            <Detail
              label="Důvod ukončení"
              value={segment.early_termination_reason ?? 'neuvedeno'}
            />
          </>
        ) : null}
      </dl>
      <section aria-label="Vozidla úseku">
        <h5>Vozidla</h5>
        {segment.vehicles.length === 0 ? <p>Neuvedeno</p> : null}
        {segment.vehicles.length > 0 ? (
          <ul>
            {segment.vehicles.map((vehicle) => (
              <li key={vehicle.vehicle_id}>
                {vehicle.series}
                {vehicle.full_number === null ? '' : ` · ${vehicle.full_number}`}
                {vehicle.nickname === null ? '' : ` · ${vehicle.nickname}`}
                {vehicle.operator === null ? '' : ` · ${vehicle.operator}`}
                {` · ${vehicleTypeLabel(vehicle.vehicle_type)}`}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      {segment.note === '' ? null : (
        <p>
          <strong>Poznámka:</strong> {segment.note}
        </p>
      )}
      {segment.tags.length === 0 ? null : (
        <p>
          <strong>Štítky:</strong> {segment.tags.join(', ')}
        </p>
      )}
      <div className="form-actions segment-actions">
        <button
          type="button"
          className="secondary-action touch-target"
          disabled={editingDisabled}
          onClick={onEdit}
        >
          Upravit úsek
        </button>
        <button
          type="button"
          className="secondary-action touch-target"
          disabled={editingDisabled}
          onClick={onCopy}
        >
          Kopírovat jako nový
        </button>
        <button
          type="button"
          className="secondary-action touch-target"
          disabled={editingDisabled}
          onClick={onReverse}
        >
          Obrátit směr jako nový
        </button>
        <button type="button" className="secondary-action touch-target" onClick={onSaveTemplate}>
          Uložit jako šablonu
        </button>
      </div>
    </article>
  );
}

function Detail({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function momentLabel(moment: TripSegment['actual_start']): string {
  if (moment === null) return 'neuvedeno';
  return `${moment.instant.slice(0, 16).replace('T', ' ')} · ${moment.utc_offset} · ${moment.time_zone}`;
}

function statusLabel(status: TripSegment['status']): string {
  return {
    cancelled: 'zrušený',
    complete: 'dokončený',
    draft: 'rozpracovaný',
    partial: 'předčasně ukončený',
  }[status];
}

function workTypeLabel(value: TripSegment['work_type']): string {
  if (value === null) return 'neuvedeno';
  return {
    freight: 'nákladní doprava',
    light_engine: 'samostatná jízda hnacího vozidla',
    other: 'jiný výkon',
    passenger: 'osobní doprava',
    service_run: 'služební jízda',
    shunting: 'posun',
  }[value];
}

function roleLabel(value: TripSegment['role']): string {
  if (value === null) return 'neuvedeno';
  return {
    driver: 'strojvedoucí',
    instructor: 'instruktor',
    other: 'jiná role',
    second_driver: 'druhý strojvedoucí',
    transport: 'přeprava',
  }[value];
}

function vehicleTypeLabel(value: TripSegment['vehicles'][number]['vehicle_type']): string {
  return {
    driving_trailer: 'řídicí vůz',
    locomotive: 'lokomotiva',
    multiple_unit: 'jednotka',
    other: 'jiné vozidlo',
    railcar: 'motorový vůz',
  }[value];
}

function distanceProvenanceLabel(segment: TripSegment): string {
  switch (segment.route_snapshot?.distance.provenance) {
    case 'manual':
      return 'ručně potvrzeno';
    case 'network_graph':
      return 'železniční graf';
    case 'trusted_import':
      return 'důvěryhodný import';
    case 'unknown':
    case undefined:
      return 'neznámý původ';
  }
}

function routePathLabel(segment: TripSegment): string {
  const snapshot = segment.route_snapshot;
  if (snapshot === null) return 'neuvedeno';
  return [
    snapshot.origin.display_name,
    ...snapshot.via.map((location) => location.display_name),
    snapshot.destination.display_name,
  ].join(' → ');
}

function routeSourceLabel(segment: TripSegment): string {
  switch (segment.route_snapshot?.candidate_source) {
    case 'history':
      return 'dříve použitá trasa';
    case 'template':
      return 'uložená šablona';
    case 'network':
      return 'návrh železničního grafu';
    case 'manual':
      return 'ručně sestavená trasa';
    case undefined:
      return 'neuvedeno';
  }
}
